export default function ImplementationTimeline() {
  const phases = [
    {
      week: 'Week 1-2',
      phase: 'Initial Consultation',
      icon: 'ri-discuss-line',
      bgColor: 'bg-[#4A7BFF]',
      activities: [
        'Comprehensive needs assessment',
        'School infrastructure evaluation',
        'Budget and timeline discussion',
        'Customized proposal preparation'
      ],
      deliverable: 'Detailed proposal document',
      responsible: 'STEMForge Team'
    },
    {
      week: 'Week 3-4',
      phase: 'Agreement & Planning',
      icon: 'ri-file-text-line',
      bgColor: 'bg-[#00C853]',
      activities: [
        'Contract finalization and signing',
        'Project timeline confirmation',
        'Team assignment and introduction',
        'Detailed implementation plan'
      ],
      deliverable: 'Signed agreement & project plan',
      responsible: 'Joint (School + STEMForge)'
    },
    {
      week: 'Week 5-8',
      phase: 'Infrastructure Setup',
      icon: 'ri-building-line',
      bgColor: 'bg-[#9C27FF]',
      activities: [
        'Lab space preparation and construction',
        'Equipment procurement and installation',
        'Network and electrical setup',
        'Safety audit and certification'
      ],
      deliverable: 'Fully equipped STEM lab',
      responsible: 'STEMForge Team'
    },
    {
      week: 'Week 9-10',
      phase: 'Teacher Training',
      icon: 'ri-graduation-cap-line',
      bgColor: 'bg-[#FF6B00]',
      activities: [
        'Intensive 40-hour training program',
        'Equipment operation training',
        'Curriculum familiarization',
        'Practice teaching sessions'
      ],
      deliverable: 'Certified teaching staff',
      responsible: 'STEMForge Team'
    },
    {
      week: 'Week 11-12',
      phase: 'Soft Launch',
      icon: 'ri-rocket-line',
      bgColor: 'bg-[#4A7BFF]',
      activities: [
        'Pilot classes with selected students',
        'Feedback collection and analysis',
        'System adjustments and fine-tuning',
        'Parent orientation sessions'
      ],
      deliverable: 'Pilot program report',
      responsible: 'Joint (School + STEMForge)'
    },
    {
      week: 'Week 13+',
      phase: 'Full Rollout',
      icon: 'ri-flag-line',
      bgColor: 'bg-[#00C853]',
      activities: [
        'Complete student enrollment',
        'Regular classes commence',
        'Ongoing support and monitoring',
        'Quarterly performance reviews'
      ],
      deliverable: 'Operational STEM program',
      responsible: 'Joint (School + STEMForge)'
    }
  ];

  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-block px-4 py-2 bg-blue-100 text-blue-700 text-sm font-semibold uppercase tracking-wider rounded-full mb-4">
            Implementation Process
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            From Consultation to Launch
          </h2>
          <p className="text-base text-gray-600 max-w-3xl mx-auto">
            A structured 12-16 week implementation process with clear milestones and deliverables
          </p>
        </div>

        {/* Horizontal Phase Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {phases.map((phase, idx) => (
            <div
              key={idx}
              className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100 hover:shadow-xl transition-all"
            >
              {/* Phase Header - Colored banner */}
              <div className={`${phase.bgColor} p-4 relative`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                      <i className={`${phase.icon} text-xl text-white`}></i>
                    </div>
                    <div>
                      <div className="text-white/80 text-xs font-medium">{phase.week}</div>
                      <h3 className="text-lg font-bold text-white">{phase.phase}</h3>
                    </div>
                  </div>
                  {/* Phase number badge */}
                  <div className="bg-white/20 backdrop-blur-sm rounded-lg px-3 py-1">
                    <div className="text-lg font-bold text-white">{idx + 1}</div>
                  </div>
                </div>
              </div>

              <div className="p-5">
                {/* Activities */}
                <ul className="space-y-2 mb-5">
                  {phase.activities.map((activity, aIdx) => (
                    <li key={aIdx} className="flex items-start gap-2 text-sm text-gray-700">
                      <i className="ri-checkbox-circle-fill text-green-500 flex-shrink-0 mt-0.5"></i>
                      <span>{activity}</span>
                    </li>
                  ))}
                </ul>

                {/* Deliverable & Responsible */}
                <div className="bg-slate-100 rounded-xl p-4">
                  <div className="mb-3">
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Key Deliverable</div>
                    <div className="font-bold text-gray-900 flex items-center gap-2">
                      <i className="ri-gift-line text-blue-600"></i>
                      {phase.deliverable}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600 pt-3 border-t border-gray-200">
                    <i className="ri-user-line text-gray-400"></i>
                    <span className="font-medium">{phase.responsible}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Flow Indicator */}
        <div className="hidden lg:flex items-center justify-center gap-2 mb-12 text-gray-400">
          <span className="text-sm font-medium">Start</span>
          <i className="ri-arrow-right-line"></i>
          <i className="ri-arrow-right-line"></i>
          <i className="ri-arrow-right-line"></i>
          <span className="text-sm font-medium">Complete Implementation</span>
        </div>

        {/* Timeline Summary */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-3xl p-12 text-center text-white">
          <h3 className="text-2xl font-bold mb-3">Total Implementation Timeline</h3>
          <div className="text-4xl font-bold mb-3">12-16 Weeks</div>
          <p className="text-base text-blue-100 mb-7">From initial consultation to full operational launch</p>
          <div className="flex flex-wrap justify-center gap-4">
            {[
              { icon: 'ri-time-line', text: 'Structured Process' },
              { icon: 'ri-team-line', text: 'Dedicated Team' },
              { icon: 'ri-shield-check-line', text: 'Quality Assured' },
              { icon: 'ri-customer-service-2-line', text: 'Continuous Support' }
            ].map((item, idx) => (
              <div key={idx} className="flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full">
                <i className={`${item.icon} text-xl`}></i>
                <span className="font-semibold text-sm">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}